import React, { useState, useMemo, useEffect, useCallback } from "react";
import styled, { css, keyframes } from "styled-components";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { makeStyles, Modal, CircularProgress, Button } from "@material-ui/core";
import AWSService from "../../../services/AWSService";
import { getErrorMessage } from "../../../helpers/functions";
import { Popper } from "@material-ui/core";
import CaseService from "../../../services/CaseService";
import JSZip from "jszip";
import CustomSelect from "../../common/CustomSelect/CustomSelect";
import PartyService from "../../../services/PartyService";
import debounce from "lodash/debounce";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import {
  OptionContainer,
  OptionName,
  OptionEmail,
  OptionCheck,
  OptionContent,
  StyledAutocomplete,
  SelectLabel,
  SelectWrapper,
} from "../NoticeGeneration/component/CustomSelect";
import {
  CustomInputField,
  CustomCheckbox,
  CheckBoxLabel,
  CheckboxContainer,
} from "../../common/FormInputs";
import useSettings from "../../../hooks/useSettings";
import useUser from "../../../hooks/useUser";
import { formatToIST } from "../../../helpers/functions";
import CaseLifeCycleStatus from "../../pages/AssignCaseLifeCycleBulkCase/CaseLifeCycleStatus";
import AddIcon from "@material-ui/icons/Add";
import {
  AddCircle,
  ChevronIcon,
  StageCard,
  StageHint,
  StageInfo,
  StageName,
  StagePlaceholder,
} from "../../pages/AssignCaseLifeCycleBulkCase";

const PAGE_SIZE = 50;

// Allowed extensions inside a ZIP and the MIME type each one is uploaded with
const EXTENSION_MIME_TYPES = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  tif: "image/tiff",
  tiff: "image/tiff",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
  txt: "text/plain",
  mp3: "audio/mpeg",
  mp4: "video/mp4",
};

// OS generated files that should never be uploaded or reported as invalid
const IGNORED_FILE_NAMES = [".ds_store", "thumbs.db", "desktop.ini"];

const VALID_MIME_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/tiff",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "video/mp4",
  "audio/mpeg",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/csv",
  "text/plain",
  "application/msword",
];

const FOLDER_TYPES = [
  { label: "Select a folder", value: "folder" },
  { label: "Select a ZIP file", value: "zipfile" },
];

const VIEWING_OPTIONS = [
  {
    label: "Share with All Parties",
    value: "all",
    desc: "Visible to all participants",
  },
  {
    label: "Share with Case Manager & Neutrals",
    value: "others",
    desc: "Visible to case manager and neutrals",
  },
  {
    label: "Share with My Team",
    value: "me",
    desc: "Visible to your team",
  },
  {
    label: "Private",
    value: "strictMode",
    desc: "Only you can view",
  },
];

const WHATSAPP_RECIPIENT_OPTIONS_BY_VISIBILITY = {
  all: [
    { label: "Claimant", value: "claimant" },
    { label: "Respondent", value: "respondent" },
    { label: "Neutrals", value: "neutrals" },
    { label: "Case Manager", value: "caseManager" },
    { label: "Co Case Manager", value: "coCaseManager" },
    { label: "All", value: "all" },
  ],
  others: [
    { label: "Neutrals", value: "neutrals" },
    { label: "Case Manager", value: "caseManager" },
    { label: "Co Case Manager", value: "coCaseManager" },
    { label: "All", value: "all" },
  ],
  me: [
    { label: "Case Manager", value: "caseManager" },
    { label: "Co Case Manager", value: "coCaseManager" },
    { label: "All", value: "all" },
  ],
};

const fadeUp = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;
const scaleIn = keyframes`from{opacity:0;transform:scale(.96)}to{opacity:1;transform:scale(1)}`;
const shimmer = keyframes`0%{background-position:-200% center}100%{background-position:200% center}`;
const pulseTeal = keyframes`0%,100%{box-shadow:0 0 0 0 rgba(0,131,140,.25)}50%{box-shadow:0 0 0 8px rgba(0,131,140,0)}`;

const useStyles = makeStyles(() => ({
  button: {
    backgroundColor: COLORS.PRIMARY_WHITE,
    boxShadow: "none",
    width: "77px",
    height: "30px",
    color: COLORS.BTN_GREEN,
    textTransform: "Capitalize",
    border: "solid 1px #00838c",
    fontFamily: theme.fonts.primaryFontBold,
    fontSize: 14,
  },
  formLabel: {
    fontFamily: theme.fonts.primaryFontSemiBold,
    color: COLORS.COLOR_DARK,
  },
  table: {
    backgroundColor: "transparent",
    border: "1px solid #e1e3ee",
    borderRadius: 7,
    "& .MuiTableCell-root": {
      borderBottom: 0,
    },
  },
  tableHead: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: COLORS.INPUT_LABEL,
    fontSize: 12,
  },
  tableBody: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: COLORS.COLOR_DARK,
    fontSize: 12,
  },
  listbox: {
    color: "#293461",
    fontFamily: "openSans-SemiBold, sans-serif",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    "& li[data-option-index]": {
      padding: "12px 16px !important",
      borderBottom: "1px solid #f3f4f6",
      transition: "all 0.2s ease",

      "&:hover": {
        backgroundColor: "#f0f9ff",
        paddingLeft: "20px !important",
      },
    },
  },
  noOptions: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#8294a5",
    padding: "16px",
    fontSize: "14px",
  },
  paper: {
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1) !important",
    borderRadius: "8px !important",
    border: "1px solid #e2e8f0 !important",
    width: "450px",
  },
}));

const CustomPopper = (props) => {
  const anchorEl = props.anchorEl;
  const width = anchorEl ? anchorEl.clientWidth : undefined;

  return (
    <Popper
      {...props}
      style={{
        ...props.style,
        width: width || undefined,
        zIndex: 1300,
      }}
      placement="bottom-start"
      modifiers={{
        flip: { enabled: true },
        preventOverflow: { enabled: true, boundariesElement: "scrollParent" },
      }}
    />
  );
};

const chunkArray = (arr, n) =>
  arr.reduce(
    (acc, _, i) => (i % n === 0 ? [...acc, arr.slice(i, i + n)] : acc),
    [],
  );

const parseNotUploaded = (data) =>
  data.map((item) => {
    const [id, ...rest] = item.split(" - ");
    return { id, filename: rest.join(" - ") };
  });

const splitPath = (path = "") =>
  path.replace(/\\/g, "/").split("/").filter(Boolean);

const getExtension = (fileName) => {
  const idx = fileName.lastIndexOf(".");
  return idx > 0 ? fileName.slice(idx + 1).toLowerCase() : "";
};

const isIgnoredPath = (segments) => {
  const fileName = segments[segments.length - 1] || "";
  return (
    segments.includes("__MACOSX") ||
    fileName.startsWith("._") ||
    fileName.startsWith("~$") ||
    IGNORED_FILE_NAMES.includes(fileName.toLowerCase())
  );
};

/**
 * @description Resolve the contract folder for a file path. The contract folder
 * is the first level under the root folder; any deeper sub-folders belong to
 * that contract. Files sitting directly inside the root folder use the root
 * folder itself as the contract (same as the old flat behaviour).
 * @param segments path split into folders + file name
 * @param hasRootFolder whether segments[0] is a wrapper folder (selected folder / single top-level folder in a ZIP)
 */
const getContractFolder = (segments, hasRootFolder) => {
  const dirs = segments.slice(0, -1);
  if (!dirs.length) return "";
  if (hasRootFolder && dirs.length > 1) return dirs[1];
  return dirs[0];
};

/**
 * @description Full upload key for a file, keeping every nested folder name
 * (e.g. "1/shs/wjjs/file.pdf"). The wrapper root folder is dropped when the
 * file sits inside a contract folder, same as getContractFolder.
 */
const getUploadPath = (segments, hasRootFolder) => {
  const dirs = segments.slice(0, -1);
  const start = hasRootFolder && dirs.length > 1 ? 1 : 0;
  return segments.slice(start).join("/");
};

// Guard against the same key appearing twice — suffix it instead of overwriting in S3
const getUniqueName = (name, usedNames) => {
  const dot = name.lastIndexOf(".");
  const hasExt = dot > name.lastIndexOf("/");
  const base = hasExt ? name.slice(0, dot) : name;
  const ext = hasExt ? name.slice(dot) : "";
  let candidate = name;
  for (let n = 1; usedNames.has(candidate.toLowerCase()); n++) {
    candidate = `${base} (${n})${ext}`;
  }
  usedNames.add(candidate.toLowerCase());
  return candidate;
};

const BulkUpload = ({
  bulkUpload,
  setBulkUpload,
  cmList,
  cmId,
  menuItems = [],
}) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const { userObject } = useUser();
  const classes = useStyles();

  const [urls, setUrls] = useState([]);
  const [view, setView] = useState("me");
  const [invalidFiles, setInvalidFiles] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [folderType, setFolderType] = useState("folder");
  const [notUploaded, setNotUploaded] = useState([]);
  const [checkerId, setCheckerId] = useState();
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [partySearchOpen, setPartySearchOpen] = useState(false);
  const [partyValue, setPartyValue] = useState(null);
  const [partyError, setPartyError] = useState("");
  const { selectMenuItems } = useSettings();
  const [noticeCategory, setNoticeCategory] = useState("");
  const [whatsappNotificationRecipients, setWhatsappNotificationRecipients] =
    useState([]);
  const [lifeCycle, setLifeCycle] = useState(false);
  const [status, setStatus] = useState([]);

  const recipientOptions =
    WHATSAPP_RECIPIENT_OPTIONS_BY_VISIBILITY[view] ||
    WHATSAPP_RECIPIENT_OPTIONS_BY_VISIBILITY.all;

  const individualRecipientValues = recipientOptions
    .map((o) => o.value)
    .filter((v) => v !== "all");

  const isAllSelected =
    individualRecipientValues.length > 0 &&
    individualRecipientValues.every((val) =>
      whatsappNotificationRecipients.includes(val),
    );

  const handleRecipientChange = (recipientValue) => {
    let nextRecipients;
    if (recipientValue === "all") {
      if (isAllSelected) {
        nextRecipients = [];
      } else {
        nextRecipients = [...individualRecipientValues];
      }
    } else {
      if (whatsappNotificationRecipients.includes(recipientValue)) {
        nextRecipients = whatsappNotificationRecipients.filter(
          (v) => v !== recipientValue,
        );
      } else {
        nextRecipients = [...whatsappNotificationRecipients, recipientValue];
      }
    }
    setWhatsappNotificationRecipients(nextRecipients);
  };

  useEffect(() => {
    setWhatsappNotificationRecipients([]);
  }, [view]);

  const debouncedFetch = useMemo(
    () =>
      debounce(async (term, page) => {
        try {
          setLoading(true);
          const q = `?search=${encodeURIComponent(term)}&page=${page}&perPage=${PAGE_SIZE}&status=active`;
          const res = await PartyService.index(q);
          if (res?.data?.length) {
            const mapped = res.data.map(({ name, id, owner }) => ({
              label: name,
              value: id,
              email: owner?.email,
            }));
            setOptions((p) => (page === 1 ? mapped : [...p, ...mapped]));
            setTotal(res.meta?.total || total);
          }
        } catch (err) {
          enqueueSnackbar(getErrorMessage(err), { variant: "error" });
          if (err.response?.status >= 500)
            setTimeout(() => debouncedFetch(term, page), 1000);
        } finally {
          setLoading(false);
        }
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [total],
  );

  useEffect(() => {
    if (partySearchOpen && inputValue.length >= 2) {
      setOptions([]);
      debouncedFetch(inputValue, 1);
    } else if (!inputValue) {
      setOptions([]);
    }
  }, [inputValue, partySearchOpen, debouncedFetch]);

  const s3Single = useCallback(
    async (file, folderName, pct) => {
      try {
        setLoader({
          state: true,
          message: `Uploading ${folderName} — ${Math.round(pct)}%`,
        });
        const s3 = await AWSService.getS3URL({ key: file.name });
        if (s3.url) {
          const res = await AWSService.uploadToS3(s3.url, file);
          setUrls((p) => [...p, res]);
        }
      } catch (err) {
        enqueueSnackbar(getErrorMessage(err), { variant: "error" });
      } finally {
        setLoader({ state: false });
      }
    },
    [setLoader, enqueueSnackbar],
  );

  const s3Batch = useCallback(
    async (batches) => {
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        try {
          setLoader({
            state: true,
            message: `Uploading batch ${i + 1} of ${batches.length}`,
          });
          const s3 = await AWSService.getS3URL({
            key: batch.map((f) => f.name),
          });
          const bUrls = await Promise.all(
            batch.map((f, idx) => AWSService.uploadToS3(s3.url[idx], f)),
          );
          setUrls((p) => [...p, ...bUrls]);
        } catch (err) {
          enqueueSnackbar(getErrorMessage(err), { variant: "error" });
        } finally {
          setLoader({ state: false });
        }
      }
    },
    [setLoader, enqueueSnackbar],
  );

  const handleFolderSelection = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    // Allow re-selecting the same folder
    event.target.value = "";
    if (!selectedFiles.length) return;
    setInvalidFiles([]);
    // Group files by contract folder, however deeply they are nested inside it
    const folderStructure = selectedFiles.reduce((acc, file) => {
      const segments = splitPath(file.webkitRelativePath || file.name);
      if (isIgnoredPath(segments)) return acc;
      const folderName = getContractFolder(segments, true);
      const path = getUploadPath(segments, true);
      if (!acc[folderName]) acc[folderName] = [];
      acc[folderName].push({ file, path });
      return acc;
    }, {});

    // Start uploading files immediately
    await uploadFiles(folderStructure);
  };

  /**
   * @description upload each file with its full nested path, e.g. "1/shs/wjjs/<file>"
   */

  const uploadFiles = async (folderStructure) => {
    const folders = Object.entries(folderStructure);
    const usedNames = new Set();
    let count = 0;
    for (const [folderName, files] of folders) {
      count++;
      let percentage = (count / folders.length) * 100;
      for (const { file, path } of files) {
        if (VALID_MIME_TYPES.includes(file.type)) {
          const newFileName = getUniqueName(path, usedNames);
          const modifiedFile = new File([file], newFileName, {
            lastModified: file.lastModified,
            type: file.type,
          });
          await s3Single(modifiedFile, folderName || file.name, percentage);
        } else {
          const fileToNotUploaded = `${folderName} - ${file?.name}`;
          setNotUploaded((prevState) => [...prevState, fileToNotUploaded]);
          setInvalidFiles((prevState) => [...prevState, file?.name]);
        }
      }
    }
    enqueueSnackbar("All files uploaded successfully!", {
      variant: "success",
    });
  };

  const handleZipUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";

    if (!file) return;

    const isZip =
      file.name.toLowerCase().endsWith(".zip") ||
      file.type === "application/zip" ||
      file.type === "application/x-zip-compressed";

    if (!isZip) {
      enqueueSnackbar(
        `"${file.name}" is not a ZIP file. Please select a valid .zip file.`,
        { variant: "error" },
      );
      return;
    }

    setLoader({ state: true, message: "Extracting ZIP file…" });
    setUrls([]);
    setInvalidFiles([]);
    setNotUploaded([]);

    const valid = [];
    const skipped = [];
    try {
      const content = await new JSZip().loadAsync(file);
      const entries = Object.values(content.files)
        .filter((entry) => !entry.dir)
        .map((entry) => ({ entry, segments: splitPath(entry.name) }))
        .filter(({ segments }) => segments.length && !isIgnoredPath(segments));

      // Zipping a parent folder wraps everything in one top-level folder;
      // in that case the contract folders are one level below it
      const topLevelFolders = new Set(
        entries.map(({ segments }) => segments[0]),
      );
      const hasRootFolder =
        topLevelFolders.size === 1 &&
        entries.every(({ segments }) => segments.length > 1);

      const usedNames = new Set();
      for (const { entry, segments } of entries) {
        const fileName = segments[segments.length - 1];
        const contract = getContractFolder(segments, hasRootFolder);
        const mimeType = EXTENSION_MIME_TYPES[getExtension(fileName)];
        if (!mimeType) {
          skipped.push({ contract, fileName });
          continue;
        }
        const blob = await entry.async("blob");
        const newName = getUniqueName(
          getUploadPath(segments, hasRootFolder),
          usedNames,
        );
        valid.push(
          new File([blob], newName, {
            type: mimeType,
            lastModified: entry.date ? entry.date.getTime() : Date.now(),
          }),
        );
      }
    } catch (err) {
      setLoader({ state: false });
      enqueueSnackbar(
        `Unable to read "${file.name}". Please check the ZIP file and try again.`,
        { variant: "error" },
      );
      return;
    }

    if (skipped.length) {
      setNotUploaded(skipped.map((s) => `${s.contract} - ${s.fileName}`));
      setInvalidFiles(skipped.map((s) => s.fileName));
    }

    const batches = chunkArray(valid, 50);
    if (!batches.length) {
      setLoader({ state: false });
      enqueueSnackbar(
        "Please upload only valid file formats inside the ZIP file",
        { variant: "error" },
      );
      return;
    }
    setLoader({ state: false });
    await s3Batch(batches);
  };

  const handleSubmit = async (values) => {
    try {
      setLoader({ state: true, message: "Please wait…" });
      const sorted = [...status].sort(
        (a, b) => new Date(a.date) - new Date(b.date),
      );
      const selectedStage = sorted[sorted.length - 1];
      const payload = {
        url: { url: urls },
        filesNotUploaded: notUploaded,
        visibilityType: view,
        checkerId,
        partyId: partyValue?.value,
        noticeCategory: noticeCategory,
        ...(selectedStage
          ? {
              caseStages: {
                status: selectedStage.value,
                currentUser: userObject?.name || "System",
                stagesDate: formatToIST(selectedStage.date),
                typeOfUpdate: "caseUpdate",
              },
            }
          : {}),
        ...(whatsappNotificationRecipients.length > 0
          ? { whatsappNotificationRecipient: whatsappNotificationRecipients }
          : {}),
      };
      const res = await CaseService.bulkUploadDocuments(payload);
      if (res) enqueueSnackbar(res.message, { variant: "success" });
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: "error" });
    } finally {
      setLoader({ state: false });
      setBulkUpload(false);
      setUrls([]);
      setInvalidFiles([]);
      setNotUploaded([]);
      setWhatsappNotificationRecipients([]);
      setStatus([]);
    }
  };

  const handleNext = () => {
    // if (!partyValue) {
    // setPartyError("Please select a party to continue");
    // return;
    // }
    setPartyError("");
    setConfirmOpen(true);
  };

  return (
    <>
      <Modal open={bulkUpload} onClose={() => setBulkUpload(false)}>
        <Overlay
          onClick={(e) => e.target === e.currentTarget && setBulkUpload(false)}
        >
          <Card wide={confirmOpen}>
            <Header>
              <HeaderLeft>
                <Title>Bulk Upload</Title>
                <Subtitle>
                  {confirmOpen
                    ? "Review before submitting"
                    : "Upload documents in bulk"}
                </Subtitle>
              </HeaderLeft>
              <HeaderRight>
                <StepPips>
                  <Pip $active={!confirmOpen} />
                  <Pip $active={confirmOpen} />
                </StepPips>
                <CloseBtn onClick={() => setBulkUpload(false)}>✕</CloseBtn>
              </HeaderRight>
            </Header>

            {confirmOpen ? (
              <>
                <Body>
                  <Banner>
                    <BannerText>
                      <BannerTitle>
                        {notUploaded.length
                          ? `${notUploaded.length} file(s) skipped due to format issues`
                          : `${urls.length} file(s) ready to upload`}
                      </BannerTitle>
                      <BannerDesc>
                        {notUploaded.length
                          ? "You can still proceed with the valid files listed above."
                          : "All files passed validation. Click Submit to continue."}
                      </BannerDesc>
                    </BannerText>
                  </Banner>

                  {notUploaded.length > 0 && (
                    <Section>
                      <SLabel>Files with issues</SLabel>
                      <TableWrap>
                        <Row
                          style={{
                            background: "#f8fafc",
                            borderBottom: "1px solid #e2e8f0",
                          }}
                        >
                          <TH>#</TH>
                          <TH>Contract No.</TH>
                          <TH>File Name</TH>
                        </Row>
                        {parseNotUploaded(notUploaded).map((row, i) => (
                          <Row key={i}>
                            <TD>{i + 1}</TD>
                            <TD>{row.id}</TD>
                            <TD>{row.filename}</TD>
                          </Row>
                        ))}
                      </TableWrap>
                    </Section>
                  )}
                </Body>

                <Footer>
                  <Btn
                    $variant="secondary"
                    onClick={() => setConfirmOpen(false)}
                  >
                    Back
                  </Btn>
                  <Btn
                    $variant="primary"
                    onClick={handleSubmit}
                    disabled={!urls.length}
                  >
                    Submit {urls.length > 0 && `(${urls.length} files)`}
                  </Btn>
                </Footer>
              </>
            ) : (
              <>
                <Body>
                  <Section>
                    <SLabel>Upload Method</SLabel>
                    <ChipRow>
                      {FOLDER_TYPES.map((ft) => (
                        <Chip
                          key={ft.value}
                          $active={folderType === ft.value}
                          onClick={() => setFolderType(ft.value)}
                          type="button"
                        >
                          {ft.label}
                        </Chip>
                      ))}
                    </ChipRow>
                  </Section>
                  <Section>
                    <SLabel>Visibility</SLabel>
                    <VisList>
                      {VIEWING_OPTIONS.map((opt) => (
                        <VisCard
                          key={opt.value}
                          $active={view === opt.value}
                          onClick={() => setView(opt.value)}
                        >
                          <VisBody>
                            <VisTitle>{opt.label}</VisTitle>
                            <VisDesc>{opt.desc}</VisDesc>
                          </VisBody>
                          <RadioRing $active={view === opt.value} />
                        </VisCard>
                      ))}
                    </VisList>
                  </Section>
                  <Section>
                    <SelectWrapper className="form-fields">
                      <SelectLabel
                        style={{
                          fontFamily: "openSans-SemiBold, sans-serif",
                          fontSize: 11,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          letterSpacing: ".8px",
                          color: "#64748b",
                        }}
                      >
                        Select a Party (Optional)
                      </SelectLabel>
                      <StyledAutocomplete
                        classes={{
                          listbox: classes.listbox,
                          noOptions: classes.noOptions,
                          paper: classes.paper,
                        }}
                        open={partySearchOpen}
                        onOpen={() => setPartySearchOpen(true)}
                        onClose={() => setPartySearchOpen(false)}
                        value={partyValue}
                        onChange={(_, val) => {
                          setPartyValue(val);
                          // setPartyError(val ? "" : "Party is required");
                        }}
                        inputValue={inputValue}
                        onInputChange={(_, val) => setInputValue(val)}
                        selectOnFocus
                        clearOnBlur
                        options={options}
                        loading={loading}
                        noOptionsText={
                          inputValue.length < 2
                            ? "Type at least 2 characters to search"
                            : loading
                              ? "Searching…"
                              : "No parties found"
                        }
                        getOptionLabel={(o) => o.label?.toString() ?? ""}
                        renderOption={(o) => (
                          <OptionContainer>
                            <OptionContent>
                              <OptionName>{o.label}</OptionName>
                              <OptionEmail>{o.email}</OptionEmail>
                            </OptionContent>
                            <OptionCheck>✓</OptionCheck>
                          </OptionContainer>
                        )}
                        renderInput={(params) => (
                          <CustomInputField
                            {...params}
                            variant="outlined"
                            placeholder="Type to search parties…"
                            error={Boolean(partyError)}
                            helperText={partyError}
                            onKeyDown={(e) => {
                              if (
                                (e.key === "Enter" || e.key === "Tab") &&
                                !partyValue
                              ) {
                                setPartyError("Party is required");
                                e.preventDefault();
                              }
                            }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loading && (
                                    <CircularProgress
                                      color="inherit"
                                      size={18}
                                    />
                                  )}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </SelectWrapper>
                  </Section>

                  <Section>
                    <SelectWrapper>
                      <SelectLabel
                        style={{
                          fontFamily: "openSans-SemiBold, sans-serif",
                          fontSize: 11,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          letterSpacing: ".8px",
                          color: "#64748b",
                        }}
                      >
                        Notice Category Name
                      </SelectLabel>

                      <StyledAutocomplete
                        id="notice-category-autocomplete"
                        classes={{
                          listbox: classes.listbox,
                          noOptions: classes.noOptions,
                          paper: classes.paper,
                        }}
                        size="small"
                        options={
                          selectMenuItems?.length
                            ? selectMenuItems
                            : [{ label: "None", value: "none" }]
                        }
                        groupBy={(option) => option.category}
                        getOptionLabel={(option) => option.name}
                        PopperComponent={CustomPopper}
                        renderInput={(params) => (
                          <CustomInputField
                            {...params}
                            placeholder="Search notice category"
                            size="small"
                            variant="outlined"
                          />
                        )}
                        renderGroup={(params) => (
                          <div style={{ padding: "8px 0" }}>
                            <div
                              style={{
                                padding: "10px 16px",
                                fontWeight: "700",
                                fontSize: "11px",
                                color: "#0f766e",
                                textTransform: "uppercase",
                                letterSpacing: "0.8px",
                                backgroundColor: "#f0fdf4",
                                borderRadius: "6px",
                                margin: "4px 8px",
                              }}
                            >
                              {params.group}
                            </div>

                            <div
                              style={{
                                padding: "4px 0",
                                color: COLORS.COLOR_DARK,
                                fontFamily: theme.fonts.primaryFontSemiBold,
                              }}
                            >
                              {params.children}
                            </div>
                          </div>
                        )}
                        noOptionsText={
                          <div
                            style={{
                              color: COLORS.INPUT_LABEL,
                              fontFamily: "openSans-SemiBold, sans-serif",
                              fontSize: "14px",
                              padding: "8px",
                            }}
                          >
                            No document type found
                          </div>
                        }
                        onChange={(event, newValue) =>
                          setNoticeCategory(newValue?.name || "")
                        }
                      />
                    </SelectWrapper>
                  </Section>
                  {/* .................. */}

                  <Section>
                    <SLabel>Checker (Optional)</SLabel>
                    <CustomSelect
                      white
                      id="checkerId"
                      menuItemValues={cmList.filter((i) => i?.value !== cmId)}
                      name="checkerId"
                      value={checkerId}
                      onChange={(e) => setCheckerId(e?.target?.value)}
                      style={{ width: "100%" }}
                    />
                  </Section>
                  <Section>
                    <SLabel>Case Status (Optional)</SLabel>
                    <StageCard
                      filled={status.length > 0}
                      onClick={() => setLifeCycle(true)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ")
                          setLifeCycle(true);
                      }}
                    >
                      <AddCircle filled={status.length > 0}>
                        <AddIcon style={{ fontSize: 18 }} />
                      </AddCircle>
                      <StageInfo>
                        {status.length ? (
                          <>
                            <StageName>
                              {status.length} stage
                              {status.length > 1 ? "s" : ""} selected
                            </StageName>
                            <StageHint>Click to modify selections</StageHint>
                          </>
                        ) : (
                          <StagePlaceholder>
                            Click to select stages to apply
                          </StagePlaceholder>
                        )}
                      </StageInfo>
                      <ChevronIcon>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </ChevronIcon>
                    </StageCard>
                  </Section>
                  <Section>
                    <SLabel>Documents</SLabel>
                    <DropZone $filled={urls.length > 0}>
                      {urls.length > 0 ? (
                        <>
                          <DropCount>{urls.length} Files Ready</DropCount>
                          <DropText>
                            Click to replace or add more files
                          </DropText>
                        </>
                      ) : (
                        <>
                          <DropText>
                            {folderType === "zipfile"
                              ? "Click to select a ZIP file"
                              : "Click to select a folder"}
                          </DropText>
                          <DropHint>
                            pdf · mp3 · mp4 · docx · csv · txt · xlsx · xls ·
                            png · jpeg · tiff
                          </DropHint>
                        </>
                      )}
                      <Button
                        style={{
                          inset: 0,
                          opacity: 0,
                          cursor: "pointer",
                          width: "100%",
                          height: "100%",
                        }}
                        variant="contained"
                        component="label"
                      >
                        {folderType === "zipfile" ? (
                          <input
                            type="file"
                            accept="application/zip"
                            multiple
                            style={{ display: "none" }}
                            onChange={handleZipUpload}
                          />
                        ) : (
                          <input
                            type="file"
                            webkitdirectory="true"
                            multiple
                            style={{ display: "none" }}
                            onChange={handleFolderSelection}
                          />
                        )}
                      </Button>
                    </DropZone>

                    {invalidFiles.length > 0 && (
                      <WarnTag>
                        {invalidFiles.length} file(s) skipped — unsupported
                        format
                      </WarnTag>
                    )}
                  </Section>
                  {view !== "strictMode" && recipientOptions.length > 0 && (
                    <Section>
                      <SLabel>WhatsApp Notification With Attachment</SLabel>
                      <RecipientOptions>
                        {recipientOptions.map((recipient) => {
                          const isChecked =
                            recipient.value === "all"
                              ? isAllSelected
                              : whatsappNotificationRecipients.includes(
                                  recipient.value,
                                );

                          return (
                            <CheckboxContainer key={recipient.value}>
                              <CustomCheckbox
                                id={`bulk-wa-${recipient.value}`}
                                inputProps={{
                                  id: `bulk-wa-${recipient.value}`,
                                }}
                                checked={isChecked}
                                onChange={() =>
                                  handleRecipientChange(recipient.value)
                                }
                              />
                              <CheckBoxLabel
                                htmlFor={`bulk-wa-${recipient.value}`}
                              >
                                {recipient.label}
                              </CheckBoxLabel>
                            </CheckboxContainer>
                          );
                        })}
                      </RecipientOptions>
                    </Section>
                  )}
                </Body>

                <Footer>
                  <Btn
                    $variant="secondary"
                    onClick={() => setBulkUpload(false)}
                  >
                    Cancel
                  </Btn>
                  <Btn
                    $variant="primary"
                    onClick={handleNext}
                    disabled={!urls.length}
                  >
                    Review & Submit
                  </Btn>
                </Footer>
              </>
            )}
          </Card>
        </Overlay>
      </Modal>
      <CaseLifeCycleStatus
        lifeCycle={lifeCycle}
        setLifeCycle={setLifeCycle}
        status={status}
        setStatus={setStatus}
      />
    </>
  );
};

export default BulkUpload;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
  animation: ${fadeUp} 0.2s ease;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: ${({ wide }) => (wide ? "680px" : "520px")};
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.07),
    0 24px 56px -12px rgba(0, 0, 0, 0.22);
  animation: ${scaleIn} 0.26s cubic-bezier(0.34, 1.56, 0.64, 1);
  transition: max-width 0.3s ease;
`;

const Header = styled.div`
  padding: 22px 28px;
  background: linear-gradient(135deg, #00838c 0%, #005f66 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div``;

const Title = styled.h2`
  margin: 0;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.3px;
`;

const Subtitle = styled.p`
  margin: 3px 0 0;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StepPips = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  padding: 5px 12px;
`;
const Pip = styled.div`
  width: ${({ active }) => (active ? "20px" : "8px")};
  height: 8px;
  border-radius: 6px;
  background: ${({ active }) => (active ? "#fff" : "rgba(255,255,255,.35)")};
  transition: all 0.3s ease;
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 22px;
  animation: ${fadeUp} 0.3s ease;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 10px;
  }
`;

const Footer = styled.div`
  padding: 16px 28px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafafa;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const SLabel = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #64748b;
`;

const RecipientOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-top: 2px;
`;

const ChipRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;
const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 16px;
  border-radius: 10px;
  border: 1.5px solid ${({ $active }) => ($active ? "#00838c" : "#e2e8f0")};
  background: ${({ $active }) => ($active ? "#f0fdfa" : "#fff")};
  color: ${({ $active }) => ($active ? "#00838c" : "#64748b")};
  font-family: openSans-SemiBold, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  ${({ $active }) =>
    $active &&
    css`
      animation: ${pulseTeal} 1.5s ease-out 1;
    `}
  &:hover {
    border-color: #00838c;
    color: #00838c;
    background: #f0fdfa;
  }
`;

const VisList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const VisCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 13px 16px;
  border-radius: 12px;
  border: 1.5px solid ${({ $active }) => ($active ? "#00838c" : "#e2e8f0")};
  background: ${({ $active }) => ($active ? "#f0fdfa" : "#fafafa")};
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: #00838c;
    background: #f0fdfa;
  }
`;
const VisBody = styled.div`
  flex: 1;
`;
const VisTitle = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #293461;
`;
const VisDesc = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
`;
const RadioRing = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 2px solid ${({ $active }) => ($active ? "#00838c" : "#cbd5e1")};
  background: ${({ $active }) => ($active ? "#00838c" : "transparent")};
  position: relative;
  transition: all 0.15s;
  &::after {
    content: "";
    position: absolute;
    inset: 3px;
    border-radius: 50%;
    background: #fff;
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    transition: opacity 0.15s;
  }
`;

const DropZone = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 32px 20px;
  border-radius: 14px;
  background: ${({ $filled }) => ($filled ? "#f0fdfa" : "#fafafa")};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  ${({ $filled }) =>
    $filled &&
    css`
      &::before {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(0, 131, 140, 0.06) 50%,
          transparent 100%
        );
        background-size: 200% auto;
        animation: ${shimmer} 2s linear infinite;
      }
    `}
  &:hover {
    border-color: #00838c;
    background: #f0fdfa;
  }
`;

const DropCount = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #00838c;
  z-index: 1;
`;
const DropText = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 13px;
  color: #64748b;
  z-index: 1;
  text-align: center;
`;
const DropHint = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 11px;
  color: #94a3b8;
  z-index: 1;
  text-align: center;
`;

const WarnTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  padding: 7px 12px;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 12px;
  color: #c2410c;
  font-weight: 700;
`;

const Banner = styled.div`
  background: linear-gradient(135deg, #f0fdfa, #e6f7f8);
  border: 1px solid #99d9dd;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const BannerText = styled.div``;

const BannerTitle = styled.p`
  margin: 0 0 3px;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #0f4c52;
`;
const BannerDesc = styled.p`
  margin: 0;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 12px;
  color: #2d8a92;
`;

const TableWrap = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  max-height: 280px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 10px;
  }
`;
const Row = styled.div`
  display: grid;
  grid-template-columns: 50px 1fr 2fr;
  padding: 10px 16px;
  transition: background 0.1s;
  border-bottom: 1px solid #f1f5f9;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #f8fafc;
  }
`;
const TH = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
`;
const TD = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 12px;
  color: #475569;
  font-weight: 600;
  word-break: break-all;
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 10px 22px;
  border-radius: 10px;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 2px solid transparent;
  outline: none;
  ${({ $variant }) =>
    $variant === "primary"
      ? css`
          background: #00838c;
          color: #ffffff;
          &:disabled {
            opacity: 0.4;
            cursor: not-allowed;
            transform: none;
          }
        `
      : css`
          background: #d91b1b;
          border-color: #d91b1b;
          color: #ffffff;
        `}
`;
